import { MEDIA_KINDS, MEDIA_STATUSES, type MediaKind, type MediaStatus } from '@/types/api'

export const MEDIA_SORTS = ['-created_at', 'created_at', 'name', '-name', '-size_bytes', 'size_bytes'] as const
export type MediaSort = (typeof MEDIA_SORTS)[number]
export const DEFAULT_MEDIA_SORT: MediaSort = '-created_at'

export const SORT_LABELS: Record<MediaSort, string> = {
  '-created_at': 'Newest first',
  created_at: 'Oldest first',
  name: 'Name (A–Z)',
  '-name': 'Name (Z–A)',
  '-size_bytes': 'Largest first',
  size_bytes: 'Smallest first',
}

/** `/media` search params — the library view is fully described by the URL. */
export interface MediaSearch {
  q?: string
  kind?: MediaKind
  /** Folder id, or `root` for unfiled media. Absent = all media. */
  folder?: string
  tag_id?: string
  status?: MediaStatus
  sort?: MediaSort
  /** Trash view. */
  trash?: true
  /** Open media detail drawer (deep link). */
  media?: string
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim().length > 0 ? v.trim() : typeof v === 'number' ? String(v) : undefined)
const flag = (v: unknown): true | undefined => (v === true || v === 'true' || v === 1 || v === '1' ? true : undefined)
const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined => (list.includes(v as T) ? (v as T) : undefined)

export function validateMediaSearch(s: Record<string, unknown>): MediaSearch {
  const trash = flag(s.trash)
  return {
    q: str(s.q),
    kind: oneOf(MEDIA_KINDS, s.kind),
    // Trash is a flat view — folders don't apply.
    folder: trash ? undefined : str(s.folder),
    tag_id: str(s.tag_id),
    status: oneOf(MEDIA_STATUSES, s.status),
    sort: oneOf(MEDIA_SORTS, s.sort),
    trash,
    media: str(s.media),
  }
}

/** Query parameters for `GET /media`. */
export interface MediaListParams {
  q?: string
  kind?: MediaKind
  folder_id?: string
  recursive?: 1
  tag_id?: string
  status?: MediaStatus
  trashed?: 'only'
  sort: MediaSort
}

export function toListParams(s: MediaSearch): MediaListParams {
  const params: MediaListParams = { sort: s.sort ?? DEFAULT_MEDIA_SORT }
  if (s.q) params.q = s.q
  if (s.kind) params.kind = s.kind
  if (s.tag_id) params.tag_id = s.tag_id
  if (s.status) params.status = s.status
  if (s.trash) params.trashed = 'only'
  else if (s.folder) params.folder_id = s.folder
  return params
}

/** Filters that narrow the list (not navigation: folder / trash / sort / open drawer). */
export function activeFilterCount(s: MediaSearch): number {
  return [s.q, s.kind, s.tag_id, s.status].filter(Boolean).length
}

/** Search with filters cleared but location (folder/trash) and sort kept. */
export function clearFilters(s: MediaSearch): MediaSearch {
  return { folder: s.folder, trash: s.trash, sort: s.sort }
}

export type MediaLocation = { type: 'all' } | { type: 'unfiled' } | { type: 'folder'; id: string } | { type: 'trash' }

export function locationOf(s: MediaSearch): MediaLocation {
  if (s.trash) return { type: 'trash' }
  if (s.folder === 'root') return { type: 'unfiled' }
  if (s.folder) return { type: 'folder', id: s.folder }
  return { type: 'all' }
}

/** Folder new uploads land in for the current view (null = unfiled). */
export function uploadFolderFor(s: MediaSearch): string | null {
  const loc = locationOf(s)
  return loc.type === 'folder' ? loc.id : null
}
